import { useState, useRef, useEffect } from 'react';
import { X, Check, ChevronDown, Shield, Lock, Eye } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import legalContent from '@/config/legal';

/**
 * AcknowledgmentModal - Legal scroll-gate with sequential unlocking
 * Each section must be scrolled to bottom before checkbox activates
 * All three must be green before submit button enables
 */
export function AcknowledgmentModal({ 
  type = 'client', 
  storageKey,
  onAccept, 
  onDecline 
}) {
  const { language } = useLanguage();
  const termsRef = useRef(null);
  const privacyRef = useRef(null);
  const ndaRef = useRef(null);
  
  const [activeSection, setActiveSection] = useState('terms');
  const [scrolledSections, setScrolledSections] = useState({
    terms: false,
    privacy: false,
    nda: false,
  });
  const [checkedSections, setCheckedSections] = useState({
    terms: false,
    privacy: false,
    nda: false,
  });

  const labels = {
    en: {
      title: 'Terms & Conditions',
      subtitle: 'Please review and accept to proceed',
      instruction: 'Scroll through each section to unlock the checkbox',
      tabs: {
        terms: 'Terms of Service',
        privacy: 'Privacy Policy',
        nda: 'Non-Disclosure',
      },
      checkLabels: {
        terms: 'I have read and agree to the Terms of Service',
        privacy: 'I have read and agree to the Privacy Policy',
        nda: 'I have read and agree to the Non-Disclosure Agreement',
      },
      scrollHint: '↓ Scroll to bottom to unlock ↓',
      unlocked: 'Section read - check box below',
      tracking: 'By proceeding, you acknowledge that your IP address, timestamps, and device metadata may be recorded for compliance and dispute resolution purposes.',
      accept: 'I Agree & Continue',
      decline: 'Decline',
    },
    es: {
      title: 'Términos y Condiciones',
      subtitle: 'Por favor revisa y acepta para continuar',
      instruction: 'Desplázate por cada sección para desbloquear la casilla',
      tabs: {
        terms: 'Términos',
        privacy: 'Privacidad',
        nda: 'NDA',
      },
      checkLabels: {
        terms: 'He leído y acepto los Términos de Servicio',
        privacy: 'He leído y acepto la Política de Privacidad',
        nda: 'He leído y acepto el Acuerdo de No Divulgación',
      },
      scrollHint: '↓ Desplázate al final para desbloquear ↓',
      unlocked: 'Sección leída - marca la casilla abajo',
      tracking: 'Al continuar, reconoces que tu dirección IP, marcas de tiempo y metadatos del dispositivo pueden ser registrados para propósitos de cumplimiento y resolución de disputas.',
      accept: 'Acepto y Continuar',
      decline: 'Rechazar',
    },
  };

  const t = labels[language];

  // Get content based on type
  const getContent = () => {
    const isRecruit = type === 'recruit' || type === 'private';
    return {
      terms: isRecruit ? legalContent.recruitTerms[language] : legalContent.clientTerms[language],
      privacy: isRecruit ? legalContent.recruitPrivacy[language] : legalContent.clientPrivacy[language],
      nda: {
        title: language === 'en' ? 'Non-Disclosure Agreement' : 'Acuerdo de No Divulgación',
        lastUpdated: language === 'en' ? 'January 2026' : 'Enero 2026',
        sections: language === 'en' ? [
          { 
            title: '1. Definition of Confidential Information', 
            content: 'Confidential Information includes, but is not limited to: the identity of Casa de Candy staff and companions, physical locations and addresses, pricing structures and payment methods, client lists and contact information, operational procedures and protocols, any communications exchanged during the course of service, photographs, videos, or any visual documentation, and any other information designated as confidential.' 
          },
          { 
            title: '2. Obligations of Receiving Party', 
            content: 'You agree to: (a) Hold all Confidential Information in strict confidence; (b) Not disclose any Confidential Information to any third party without prior written consent; (c) Use Confidential Information only for the purpose of receiving services; (d) Not copy, reproduce, or store Confidential Information except as necessary; (e) Return or destroy all Confidential Information upon request or termination of services.' 
          },
          { 
            title: '3. Prohibited Activities', 
            content: 'You are strictly prohibited from: Taking photographs or videos of any kind during sessions or at any Casa de Candy location; Making audio recordings of conversations or interactions; Sharing screenshots or images from any Casa de Candy platform; Posting reviews or comments that could identify staff or locations; Discussing services or experiences on social media or public forums; Sharing contact information of staff with third parties.' 
          },
          { 
            title: '4. Duration', 
            content: 'This Non-Disclosure Agreement shall remain in effect indefinitely. Your obligations under this agreement survive the termination of any service relationship with Casa de Candy.' 
          },
          { 
            title: '5. Consequences of Breach', 
            content: 'Any breach of this Non-Disclosure Agreement may result in: Immediate and permanent termination of services without refund; Pursuit of legal remedies including injunctive relief; Recovery of damages, including consequential and punitive damages; Recovery of attorney fees and legal costs; Reporting to relevant authorities if applicable.' 
          },
          { 
            title: '6. Exceptions', 
            content: 'Confidential Information does not include information that: Was publicly available at the time of disclosure; Becomes publicly available through no fault of the receiving party; Was rightfully in possession of the receiving party prior to disclosure; Is required to be disclosed by law or court order (with advance notice to Casa de Candy where legally permitted).' 
          },
          { 
            title: '7. Acknowledgment', 
            content: 'By checking the acknowledgment box, you confirm that you have read, understood, and agree to be bound by this Non-Disclosure Agreement. You acknowledge that violation of this agreement may cause irreparable harm to Casa de Candy and that monetary damages may be insufficient remedy.' 
          }
        ] : [
          { 
            title: '1. Definición de Información Confidencial', 
            content: 'La Información Confidencial incluye, pero no se limita a: la identidad del personal y compañeras de Casa de Candy, ubicaciones físicas y direcciones, estructuras de precios y métodos de pago, listas de clientes e información de contacto, procedimientos y protocolos operativos, cualquier comunicación intercambiada durante el curso del servicio, fotografías, videos o cualquier documentación visual, y cualquier otra información designada como confidencial.' 
          },
          { 
            title: '2. Obligaciones de la Parte Receptora', 
            content: 'Aceptas: (a) Mantener toda la Información Confidencial en estricta confidencialidad; (b) No divulgar ninguna Información Confidencial a terceros sin consentimiento previo por escrito; (c) Usar la Información Confidencial solo con el propósito de recibir servicios; (d) No copiar, reproducir o almacenar Información Confidencial excepto cuando sea necesario; (e) Devolver o destruir toda la Información Confidencial a solicitud o al terminar los servicios.' 
          },
          { 
            title: '3. Actividades Prohibidas', 
            content: 'Está estrictamente prohibido: Tomar fotografías o videos de cualquier tipo durante las sesiones o en cualquier ubicación de Casa de Candy; Hacer grabaciones de audio de conversaciones o interacciones; Compartir capturas de pantalla o imágenes de cualquier plataforma de Casa de Candy; Publicar reseñas o comentarios que puedan identificar al personal o ubicaciones; Discutir servicios o experiencias en redes sociales o foros públicos; Compartir información de contacto del personal con terceros.' 
          },
          { 
            title: '4. Duración', 
            content: 'Este Acuerdo de No Divulgación permanecerá vigente indefinidamente. Tus obligaciones bajo este acuerdo sobreviven la terminación de cualquier relación de servicio con Casa de Candy.' 
          },
          { 
            title: '5. Consecuencias del Incumplimiento', 
            content: 'Cualquier incumplimiento de este Acuerdo de No Divulgación puede resultar en: Terminación inmediata y permanente de servicios sin reembolso; Búsqueda de recursos legales incluyendo medidas cautelares; Recuperación de daños, incluyendo daños consecuentes y punitivos; Recuperación de honorarios de abogados y costos legales; Reporte a autoridades relevantes si aplica.' 
          },
          { 
            title: '6. Excepciones', 
            content: 'La Información Confidencial no incluye información que: Era públicamente disponible al momento de la divulgación; Se vuelve públicamente disponible sin culpa de la parte receptora; Estaba legítimamente en posesión de la parte receptora antes de la divulgación; Se requiere divulgar por ley u orden judicial (con aviso previo a Casa de Candy donde sea legalmente permitido).' 
          },
          { 
            title: '7. Reconocimiento', 
            content: 'Al marcar la casilla de reconocimiento, confirmas que has leído, entendido y aceptas estar obligado por este Acuerdo de No Divulgación. Reconoces que la violación de este acuerdo puede causar daño irreparable a Casa de Candy y que los daños monetarios pueden ser un remedio insuficiente.' 
          }
        ]
      }
    };
  };

  const content = getContent();

  // Handle scroll detection for each section
  const handleScroll = (section) => {
    let ref;
    if (section === 'terms') ref = termsRef;
    else if (section === 'privacy') ref = privacyRef;
    else if (section === 'nda') ref = ndaRef;
    
    if (!ref?.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = ref.current;
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 30;
    
    if (isAtBottom && !scrolledSections[section]) {
      setScrolledSections(prev => ({ ...prev, [section]: true }));
    }
  };

  // Handle checkbox click - only works if section was scrolled
  const handleCheck = (section) => {
    if (!scrolledSections[section]) return;
    
    // Check order: terms -> privacy -> nda
    const order = ['terms', 'privacy', 'nda'];
    const sectionIndex = order.indexOf(section);
    
    // Can only check if previous sections are checked
    for (let i = 0; i < sectionIndex; i++) {
      if (!checkedSections[order[i]]) return;
    }
    
    setCheckedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // All sections must be checked to proceed
  const allChecked = checkedSections.terms && checkedSections.privacy && checkedSections.nda;

  const handleAccept = () => {
    if (!allChecked) return;
    
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify({
        accepted: true,
        timestamp: new Date().toISOString(),
        type,
      }));
    }
    
    onAccept();
  };

  const sectionColors = {
    terms: '#FF4DA6',
    privacy: '#8B5CFF',
    nda: '#27D7FF',
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#07060B]/95 backdrop-blur-xl flex items-center justify-center p-4" data-testid="acknowledgment-modal">
      <div className="w-full max-w-3xl h-[90vh] bg-[#111022] rounded-2xl border border-white/10 shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-heading text-lg sm:text-xl font-bold text-[#F4F2FF]">{t.title}</h2>
              <p className="text-xs sm:text-sm text-[#A7A3BA]">{t.subtitle}</p>
            </div>
            <button
              onClick={onDecline}
              className="p-2 text-[#A7A3BA] hover:text-[#F4F2FF] transition-colors"
              data-testid="modal-close-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          {/* Instruction */}
          <p className="text-xs text-[#27D7FF] bg-[#27D7FF]/10 rounded-lg px-3 py-2 text-center">
            {t.instruction}
          </p>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-white/10 flex-shrink-0">
          {['terms', 'privacy', 'nda'].map((section) => {
            const isActive = activeSection === section;
            const isScrolled = scrolledSections[section];
            const isChecked = checkedSections[section];
            
            return (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                className={`flex-1 py-3 px-2 sm:px-4 text-xs sm:text-sm font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 border-b-2 ${
                  isActive
                    ? 'text-white'
                    : 'text-[#A7A3BA] hover:text-[#F4F2FF] border-transparent'
                }`}
                style={{ borderColor: isActive ? sectionColors[section] : 'transparent', color: isActive ? sectionColors[section] : undefined }}
              >
                {section === 'terms' && <Shield className="w-3 h-3 sm:w-4 sm:h-4" />}
                {section === 'privacy' && <Eye className="w-3 h-3 sm:w-4 sm:h-4" />}
                {section === 'nda' && <Lock className="w-3 h-3 sm:w-4 sm:h-4" />}
                <span>{t.tabs[section]}</span>
                {isChecked && (
                  <span className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center ml-1">
                    <Check className="w-3 h-3 text-white" />
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Scrollable Content Area */}
        <div className="flex-1 overflow-hidden relative" style={{ minHeight: '200px' }}>
          {/* Terms Section */}
          <div
            className={`absolute inset-0 overflow-y-auto overscroll-contain ${activeSection === 'terms' ? 'block' : 'hidden'}`}
            ref={termsRef}
            onScroll={() => handleScroll('terms')}
            data-testid="scroll-terms"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-4 sm:p-6 pb-16">
              <h3 className="font-semibold text-lg mb-1" style={{ color: sectionColors.terms }}>
                {content.terms.title}
              </h3>
              <p className="text-xs text-[#A7A3BA] mb-6">Last Updated: {content.terms.lastUpdated}</p>
              
              <div className="space-y-6">
                {content.terms.sections.map((section, idx) => (
                  <div key={idx}>
                    <h4 className="text-[#F4F2FF] font-semibold mb-2">{section.title}</h4>
                    <p className="text-[#A7A3BA] text-sm leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
              
              <div className="h-12" /> {/* Spacer for scroll detection */}
            </div>
            
            {/* Scroll indicator */}
            {!scrolledSections.terms && (
              <div className="sticky bottom-0 left-0 right-0 py-3 bg-gradient-to-t from-[#111022] via-[#111022]/95 to-transparent text-center pointer-events-none">
                <span className="inline-flex items-center gap-2 text-xs text-[#FF4DA6] font-medium animate-bounce">
                  <ChevronDown className="w-4 h-4" />
                  {t.scrollHint}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </div>
            )}
            {scrolledSections.terms && !checkedSections.terms && (
              <div className="sticky bottom-0 left-0 right-0 py-3 bg-gradient-to-t from-[#111022] via-[#111022]/95 to-transparent text-center pointer-events-none">
                <span className="inline-flex items-center gap-2 text-xs text-green-400 font-medium">
                  {t.unlocked}
                </span>
              </div>
            )}
          </div>

          {/* Privacy Section */}
          <div
            className={`absolute inset-0 overflow-y-auto overscroll-contain ${activeSection === 'privacy' ? 'block' : 'hidden'}`}
            ref={privacyRef}
            onScroll={() => handleScroll('privacy')}
            data-testid="scroll-privacy"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-4 sm:p-6 pb-16">
              <h3 className="font-semibold text-lg mb-1" style={{ color: sectionColors.privacy }}>
                {content.privacy.title}
              </h3>
              <p className="text-xs text-[#A7A3BA] mb-6">Last Updated: {content.privacy.lastUpdated}</p>
              
              <div className="space-y-6">
                {content.privacy.sections.map((section, idx) => (
                  <div key={idx}>
                    <h4 className="text-[#F4F2FF] font-semibold mb-2">{section.title}</h4>
                    <p className="text-[#A7A3BA] text-sm leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
              
              <div className="h-12" />
            </div>
            
            {!scrolledSections.privacy && (
              <div className="sticky bottom-0 left-0 right-0 py-3 bg-gradient-to-t from-[#111022] via-[#111022]/95 to-transparent text-center pointer-events-none">
                <span className="inline-flex items-center gap-2 text-xs text-[#8B5CFF] font-medium animate-bounce">
                  <ChevronDown className="w-4 h-4" />
                  {t.scrollHint}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </div>
            )}
            {scrolledSections.privacy && !checkedSections.privacy && (
              <div className="sticky bottom-0 left-0 right-0 py-3 bg-gradient-to-t from-[#111022] via-[#111022]/95 to-transparent text-center pointer-events-none">
                <span className="inline-flex items-center gap-2 text-xs text-green-400 font-medium">
                  {t.unlocked}
                </span>
              </div>
            )}
          </div>

          {/* NDA Section */}
          <div
            className={`absolute inset-0 overflow-y-auto overscroll-contain ${activeSection === 'nda' ? 'block' : 'hidden'}`}
            ref={ndaRef}
            onScroll={() => handleScroll('nda')}
            data-testid="scroll-nda"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="p-4 sm:p-6 pb-16">
              <h3 className="font-semibold text-lg mb-1" style={{ color: sectionColors.nda }}>
                {content.nda.title}
              </h3>
              <p className="text-xs text-[#A7A3BA] mb-6">Last Updated: {content.nda.lastUpdated}</p>
              
              <div className="space-y-6">
                {content.nda.sections.map((section, idx) => (
                  <div key={idx}>
                    <h4 className="text-[#F4F2FF] font-semibold mb-2">{section.title}</h4>
                    <p className="text-[#A7A3BA] text-sm leading-relaxed">{section.content}</p>
                  </div>
                ))}
              </div>
              
              <div className="h-12" />
            </div>
            
            {!scrolledSections.nda && (
              <div className="sticky bottom-0 left-0 right-0 py-3 bg-gradient-to-t from-[#111022] via-[#111022]/95 to-transparent text-center pointer-events-none">
                <span className="inline-flex items-center gap-2 text-xs text-[#27D7FF] font-medium animate-bounce">
                  <ChevronDown className="w-4 h-4" />
                  {t.scrollHint}
                  <ChevronDown className="w-4 h-4" />
                </span>
              </div>
            )}
            {scrolledSections.nda && !checkedSections.nda && (
              <div className="sticky bottom-0 left-0 right-0 py-3 bg-gradient-to-t from-[#111022] via-[#111022]/95 to-transparent text-center pointer-events-none">
                <span className="inline-flex items-center gap-2 text-xs text-green-400 font-medium">
                  {t.unlocked}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Checkboxes */}
        <div className="p-3 sm:p-4 border-t border-white/10 space-y-2 flex-shrink-0 bg-[#0B0A12]">
          {['terms', 'privacy', 'nda'].map((section, idx) => {
            const isScrolled = scrolledSections[section];
            const isChecked = checkedSections[section];
            const order = ['terms', 'privacy', 'nda'];
            const canCheck = isScrolled && (idx === 0 || checkedSections[order[idx - 1]]);
            
            return (
              <button
                key={section}
                type="button"
                onClick={() => handleCheck(section)}
                disabled={!canCheck}
                className={`w-full flex items-center gap-3 p-2.5 sm:p-3 rounded-xl border transition-all text-left ${
                  isChecked
                    ? 'bg-green-500/10 border-green-500/50'
                    : canCheck
                      ? 'bg-[#111022] border-white/10 hover:border-white/20 cursor-pointer'
                      : 'bg-[#111022]/50 border-white/5 opacity-40 cursor-not-allowed'
                }`}
                data-testid={`check-${section}`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                  isChecked
                    ? 'bg-green-500 border-green-500'
                    : canCheck
                      ? 'border-[#A7A3BA]'
                      : 'border-[#A7A3BA]/30'
                }`}>
                  {isChecked && <Check className="w-3 h-3 text-white" />}
                </div>
                <span className={`text-xs sm:text-sm ${isChecked ? 'text-green-400' : canCheck ? 'text-[#F4F2FF]' : 'text-[#A7A3BA]/50'}`}>
                  {t.checkLabels[section]}
                </span>
              </button>
            );
          })}
        </div>

        {/* Tracking Notice */}
        <div className="px-4 sm:px-6 py-2 text-center border-t border-white/5 flex-shrink-0">
          <p className="text-[9px] sm:text-[10px] text-[#A7A3BA]/50 leading-relaxed">
            {t.tracking}
          </p>
        </div>

        {/* Actions */}
        <div className="p-3 sm:p-4 border-t border-white/10 flex gap-3 flex-shrink-0">
          <button
            onClick={onDecline}
            className="flex-1 py-2.5 sm:py-3 rounded-full border border-white/20 text-[#A7A3BA] text-sm font-medium hover:bg-white/5 transition-colors"
            data-testid="modal-decline-btn"
          >
            {t.decline}
          </button>
          <button
            onClick={handleAccept}
            disabled={!allChecked}
            className={`flex-1 py-2.5 sm:py-3 rounded-full text-sm font-semibold transition-all ${
              allChecked
                ? 'bg-gradient-to-r from-[#FF4DA6] to-[#8B5CFF] text-white hover:shadow-lg hover:shadow-[#FF4DA6]/30'
                : 'bg-[#A7A3BA]/20 text-[#A7A3BA]/50 cursor-not-allowed'
            }`}
            data-testid="modal-accept-btn"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
}

export default AcknowledgmentModal;
